import { Box } from "@mui/material";
import Typography from "components/basic/typography";
import { MarketplaceHealthQuery } from "generated/graphql";
import StatTile from "../StatTile";
import { formatNumber, formatPercent } from "../../helpers/format";

interface MarketplaceHealthProps {
  health: MarketplaceHealthQuery["marketplaceHealth"] | undefined;
}

/**
 * Counted from the real tables, not from beacons — so nothing here is lost to
 * an ad-blocker, and every number covers all history rather than starting the
 * day tracking shipped.
 */
const MarketplaceHealth: React.FC<MarketplaceHealthProps> = ({ health }) => {
  const tiles = [
    { label: "New users", value: health?.newUsers ?? 0 },
    { label: "New vendors", value: health?.newVendors ?? 0 },
    { label: "Service requests", value: health?.serviceRequests ?? 0 },
    { label: "Quotes sent", value: health?.quotesSent ?? 0 },
    {
      label: "Quotes approved",
      value: health?.quotesApproved ?? 0,
      detail: `${formatPercent(
        health?.quotesApproved ?? 0,
        health?.quotesSent ?? 0,
      )} of quotes sent`,
    },
    { label: "Meetings booked", value: health?.bookings ?? 0 },
  ];

  return (
    <Box>
      <Typography variant="subtitle1" style={{ fontWeight: 600 }}>
        Marketplace health
      </Typography>
      <Typography variant="body2" sx={{ color: "text.secondary" }}>
        Counted from live records, so these are exact and cover all history.
      </Typography>

      <Box
        sx={{
          display: "grid",
          gap: 2,
          marginTop: 2,
          gridTemplateColumns: {
            xs: "repeat(2, 1fr)",
            md: "repeat(3, 1fr)",
            lg: "repeat(6, 1fr)",
          },
        }}
      >
        {tiles.map((tile) => (
          <StatTile
            key={tile.label}
            label={tile.label}
            value={formatNumber(tile.value)}
            detail={tile.detail}
          />
        ))}
      </Box>
    </Box>
  );
};

export default MarketplaceHealth;
